// frontend/src/design-system/visual.ts
//
// "Pearl + Coral" premium token strings. These class bundles are imported
// across the app; updating them here propagates the new aesthetic to every
// page that consumed VISUAL.* tokens previously.
//
// New PDS_* exports follow the premium-dashboards vocabulary (BentoTile,
// glass cards, pill stack). Both shapes are kept available so existing
// imports keep working.

export const VISUAL = {
  /** Base card surface — pearl glass, soft shadow, no harsh border. */
  card:
    'rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-white/40 dark:ring-white/10',
  /** Hero card — bigger radius and padding. */
  heroCard:
    'rounded-4xl bg-white/78 dark:bg-white/[0.06] backdrop-blur-xl shadow-card-lg ring-1 ring-white/40 dark:ring-white/10 p-8',
  /** Soft hover lift. */
  cardHover:
    'transition-all duration-300 hover:-translate-y-px hover:shadow-card-lg',
  /** Glass surface — translucent + blur (alias). */
  glass:
    'bg-white/60 dark:bg-white/[0.05] backdrop-blur-xl ring-1 ring-white/40 dark:ring-white/10 shadow-card',
  /** Editorial wordmark (kept for marketing headers). */
  serifTitle:
    'font-sans font-light text-3xl tracking-[-0.025em] text-foreground',
  /** Editorial hero numeral (StatCard values — bounded, was display-xl/88px). */
  heroNumber:
    'font-sans font-light text-stat-hero text-foreground tabular-nums',
  /** Eyebrow caption. */
  eyebrow:
    'text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55',

  /* ---------- Dashboard tokens ---------- */
  dashTitle:
    'font-sans font-light text-display-page text-foreground',
  dashSection:
    'font-sans font-medium text-title-lg text-foreground',
  dashBigNumber:
    'font-sans font-bold text-stat-lg tabular-nums text-foreground',
  dashMidNumber:
    'font-sans font-bold text-stat-md tabular-nums text-foreground',

  /** Bento tile — pearl glass, no visible border. */
  bento:
    'rounded-3xl bg-white/72 dark:bg-white/[0.05] backdrop-blur-xl shadow-card ring-1 ring-white/40 dark:ring-white/10',
  bentoGlass:
    'rounded-3xl bg-white/55 dark:bg-white/[0.04] backdrop-blur-xl shadow-card ring-1 ring-white/30 dark:ring-white/10',
  /** Contrast island — dark ink card on pearl canvas. */
  bentoDark:
    'rounded-3xl bg-ink-900 text-white shadow-card-lg ring-1 ring-white/10',
  /** Accent island — coral gradient card. */
  bentoCoral:
    'rounded-3xl bg-coral-gradient text-white shadow-cta ring-1 ring-white/10',
} as const;

export const PDS = VISUAL; // alias for new code

export type StatusTone =
  | 'success' | 'warning' | 'destructive' | 'info'
  | 'muted'   | 'saffron' | 'paid'   | 'overdue' | 'draft' | 'sent';

export function statusPill(tone: StatusTone): string {
  switch (tone) {
    case 'success':
    case 'paid':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-lime-neon text-ink ring-1 ring-ink/10 dark:bg-lime-neon/20 dark:text-lime-neon dark:ring-lime-neon/40';
    case 'warning':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-coral-100 text-coral-700 ring-1 ring-coral-200 dark:bg-coral-500/20 dark:text-coral-200 dark:ring-coral-500/40';
    case 'destructive':
    case 'overdue':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-rose-500/10 text-rose-700 ring-1 ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-200 dark:ring-rose-500/40';
    case 'info':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-lavender-100 text-lavender-700 ring-1 ring-lavender-200 dark:bg-lavender-400/20 dark:text-lavender-200 dark:ring-lavender-400/40';
    case 'saffron':
    case 'sent':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-coral/12 text-coral-700 ring-1 ring-coral/25 dark:bg-coral/20 dark:text-coral-200 dark:ring-coral/40';
    case 'draft':
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-ink/8 text-ink/65 ring-1 ring-ink/10 dark:bg-white/10 dark:text-white/70 dark:ring-white/15';
    case 'muted':
    default:
      return 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.10em] bg-ink/5 text-ink/65 ring-1 ring-ink/10 dark:bg-white/[0.06] dark:text-white/65 dark:ring-white/12';
  }
}

export function statusDot(tone: StatusTone): string {
  switch (tone) {
    case 'success':
    case 'paid':        return 'size-1.5 rounded-full bg-lime-neon dark:bg-lime-neon';
    case 'warning':     return 'size-1.5 rounded-full bg-coral dark:bg-coral-400';
    case 'destructive':
    case 'overdue':     return 'size-1.5 rounded-full bg-rose-500 dark:bg-rose-400';
    case 'info':        return 'size-1.5 rounded-full bg-lavender-400 dark:bg-lavender-300';
    case 'saffron':
    case 'sent':        return 'size-1.5 rounded-full bg-coral dark:bg-coral-400';
    case 'draft':       return 'size-1.5 rounded-full bg-ink/40 dark:bg-white/40';
    case 'muted':
    default:            return 'size-1.5 rounded-full bg-ink/40 dark:bg-white/40';
  }
}
